import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React from 'react';
import { Container } from 'react-bootstrap';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { isEmpty, timeToPeriod } from '../../helper/utils';

const Maps = ({ facilities, facilityData }) => {
  const getCircleColor = (facility) => {
    if (facility.status === "upcoming") {
      return "grey"; // Grey for upcoming facilities
    }
  
    // Use getLoadInfoByFacility to determine if there's load data
    const loadInfo = getLoadInfoByFacility(facility);
    
    // If load data is available, use green; otherwise, use black
    return loadInfo ? "green" : "black";
  };
  

  const createIcon = (index, color) => {
    return L.divIcon({
      html: `<div style="background-color:${color}; border-radius:50%; width:24px; height:24px; display:flex; align-items:center; justify-content:center; color:white; font-size:10px; font-weight:bold;">${index + 1}</div>`,
      className: '',
    });
  };

  const getCircle = (colour, number) => (
    <Container
      className='p-0 m-0 flex-grow-0'
      style={{
        backgroundColor: colour,
        borderRadius: '50%',
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '10px',
        fontWeight: 'bold'
      }}
    >
      {number}
    </Container>
  );

  const getLoadInfoByFacility = (facility) => {
    if (isEmpty(facilityData) || !facility || !facility.shortName) {
      return null;
    }
  
    // Find all entries in facilityData that start with or match the facility's shortName
    const matchingKeys = Object.keys(facilityData).filter(key => key.startsWith(facility.shortName));
  
    // Aggregate or pick data from matching entries
    if (matchingKeys.length > 0) {
      // Check if data exists and has length before trying to access it
      const loadEntries = matchingKeys
        .filter(key => facilityData[key]?.data && facilityData[key].data.length > 0)
        .map(key => facilityData[key].data[facilityData[key].data.length - 1]);
      
      // Only proceed if we have valid entries
      if (loadEntries.length === 0) {
        return null;
      }
      
      // Combine entries if multiple sources are available, otherwise return single entry
      const combinedLoadInfo = loadEntries.reduce((acc, entry) => {
        // Make sure entry is defined before accessing properties
        if (entry) {
          acc.power_kw_field = (acc.power_kw_field || 0) + (entry.power_kw_field || 0); // Sum up power
          acc.time = entry.time; // Use the latest time available
        }
        return acc;
      }, {});
  
      return combinedLoadInfo;
    }
  
    return null;
  };
  
 const getLoadByFacilityJsx = (facility, showLastUpdated = true) => {
    const loadInfo = getLoadInfoByFacility(facility);
    if (loadInfo && loadInfo.power_kw_field !== undefined && loadInfo.time) {
      let LoadJsx = <span>Load: {Math.round(loadInfo.power_kw_field)} KW</span>;
      if (showLastUpdated) {
        LoadJsx = (
          <span>
            {LoadJsx}
            <em>&nbsp;(last updated: period {timeToPeriod(loadInfo.time)} | {loadInfo.time})</em>
          </span>
        );
      }
      return LoadJsx;
    }
    return <span>No load data available</span>;
  };
  

  return (
    <>
      <MapContainer
        center={[1.3521, 104.03]}
        zoom={11}
        style={{ height: "100%", width: "100%", zIndex: 0 }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {
          facilities.map((facility, index) => (
            <Marker
              key={facility.shortName}
              position={facility.position}
              icon={createIcon(index, getCircleColor(facility))}
            >
              <Popup>
                <p className='m-0 text-center'><strong>{facility.fullName}</strong></p>
                <p className='m-0 text-center'>{getLoadByFacilityJsx(facility) || "No data available"}</p>
              </Popup>
            </Marker>
          ))
        }

      </MapContainer>
      <Container className='invisible w-50 h-100 position-absolute top-0 start-50 p-3 pt-2 d-flex justify-content-center align-items-center'>
        <Container fluid
          className='visible d-flex flex-column gap-2 p-3 pt-2 flex-grow-0 overflow-hidden'
          style={{background: "rgba(var(--sp-dark-blue-rgb), 0.8)", maxHeight: "100%"}}
        >
          <Container fluid className='p-0 overflow-auto d-flex flex-column row-gap-2 flex-grow-0'>
            <Container fluid className='p-0 d-flex flex-column'>
              <h3 className='m-0 text-light text-center'>Client Sites</h3>
              {facilities.filter(facility => facility.status === "live").map((facility, index) => (
                <Container fluid
                  className={`h-100 px-0 py-1 d-flex flex-row justify-content-start align-items-center column-gap-2 ${index === 0 ? "" : "border-top"}`}
                  key={facility.shortName}
                >
                  {getCircle(getCircleColor(facility), index+1)}
                  <Container className='h-100 w-auto p-0 flex-grow-1'>
                    <p className='text-light m-0'>{facility.fullName}</p>
                    <p className='text-light m-0'><i>{getLoadByFacilityJsx(facility)}</i></p>
                  </Container>
                </Container>
              ))}
            </Container>
            <Container fluid className='p-0 d-flex flex-column gap-1'>
              <h3 className='m-0 text-light text-center'>Upcoming Sites</h3>
              {facilities.filter(facility => facility.status === "upcoming").map((facility, index) => (
                <Container fluid
                  className={`h-100 px-0 py-1 d-flex flex-row justify-content-start align-items-center column-gap-2 ${index === 0 ? "" : "border-top"}`}
                  key={facility.shortName}
                >
                  {getCircle(
                    getCircleColor(facility),
                    facilities.filter(facility => facility.status === "live").length + index + 1
                  )}
                  <Container className='h-100 w-auto p-0 flex-grow-1'>
                    <p className='text-light m-0'>{facility.fullName}</p>
                  </Container>
                </Container>
              ))}
            </Container>
          </Container>
          <Container fluid className='p-0 d-flex flex-column justify-content-center align-content-start'>
            <p className='w-100 mb-1 text-light'><u>Legend:</u></p>
            <Container fluid className='p-0 d-flex flex-row justify-content-evenly align-content-center'>
              <Container className='p-0 d-flex flex-row justify-content-center align-content-center column-gap-2'>
                {getCircle("green", "")}
                <p className='text-light m-0'>&nbsp;Online</p>
              </Container>
              <Container className='p-0 d-flex flex-row justify-content-center align-content-center column-gap-2'>
                {getCircle("black", "")}
                <p className='text-light m-0'>&nbsp;Offline</p>
              </Container>
              <Container className='p-0 d-flex flex-row justify-content-center align-content-center column-gap-2'>
                {getCircle("grey", "")}
                <p className='text-light m-0'>&nbsp;Upcoming</p>
              </Container>
            </Container>
          </Container>
        </Container>
      </Container>
    </>
  );
};

export default Maps;